import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import {
  setShowFreeSpinRetrigger,
  setCriticalAnimationPlaying,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Device and orientation-specific size configuration
const RETRIGGER_SIZE_CONFIG = {
  MOBILE: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
    },
  },
  TABLET: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
    },
  },
  DESKTOP: {
    LANDSCAPE: {
      spineScaleMultiplier: 0.8,
    },
  },
};

// Animation name in the dialogs spine
const RETRIGGER_ANIMATION = "free_spin_retrigger";

/**
 * FreeSpinRetriggerController - Handles free spin retrigger animation
 * Plays a single animation to completion (no user interaction required).
 * Modeled on FreeSpinIntroDialogController / FreeSpinOutroDialogController.
 */
class FreeSpinRetriggerController {
  constructor(
    container,
    dimensions,
    dispatch,
    symbolHeight,
    overlayRef,
    device,
    orientation,
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.device = device;
    this.orientation = orientation;

    this.retriggerSpine = null;
    this.isPlaying = false;
    this.initialized = false;

    this._showRAF = null;

    this.initializeSpine();
  }

  // ===================================================================
  //  SPINE INITIALIZATION
  // ===================================================================

  initializeSpine() {
    try {
      this.retriggerSpine = this.createSpine();
      if (!this.retriggerSpine) return;

      const missingComponents = [];
      if (!this.retriggerSpine.skeleton) missingComponents.push("skeleton");
      if (!this.retriggerSpine.state) missingComponents.push("state");
      if (!this.retriggerSpine.skeleton?.data)
        missingComponents.push("skeleton.data");
      if (missingComponents.length > 0) return;

      this.container.addChild(this.retriggerSpine);
      this.retriggerSpine.alpha = 0.001;
      this.retriggerSpine.zIndex = 20;
      this.applyScaleAndCenter();

      if (this.retriggerSpine.parent !== this.container) return;
      this.initialized = true;

      this.warmUpSpine();
    } catch {
      // Silent fail
    }
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "dialogs-skeleton";
      const atlasAlias = "dialogs-atlas";
      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });
      if (!spine?.skeleton?.data?.animations) return null;

      return spine;
    } catch {
      return null;
    }
  }

  warmUpSpine() {
    if (!this.retriggerSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.includes(RETRIGGER_ANIMATION)) {
        this.retriggerSpine.state.setAnimation(
          0,
          RETRIGGER_ANIMATION,
          false,
        );
        this.retriggerSpine.state.update(0.016);
        this.retriggerSpine.skeleton.updateWorldTransform();
      }
      this.retriggerSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    return (
      this.retriggerSpine?.skeleton?.data?.animations.map((a) => a.name) || []
    );
  }

  // ===================================================================
  //  POSITIONING
  // ===================================================================

  getScaleMultiplier() {
    const deviceKey = this.device?.toUpperCase();
    const orientationKey = this.orientation?.toUpperCase();

    const deviceConfig = RETRIGGER_SIZE_CONFIG[deviceKey];
    if (!deviceConfig) return 1.0;

    const config = deviceConfig[orientationKey] || deviceConfig;
    return config?.spineScaleMultiplier ?? 1.0;
  }

  applyScaleAndCenter() {
    if (!this.retriggerSpine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    const baseScale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));
    const multiplier = this.getScaleMultiplier();
    const finalScale = baseScale * multiplier;

    this.retriggerSpine.scale.set(finalScale);
    this.retriggerSpine.x = this.dimensions.width / 2;
    this.retriggerSpine.y =
      this.dimensions.height / 2 -
      (this.symbolHeight || 0) -
      this.symbolHeight * 0.2;
  }

  updateDimensions(dimensions, symbolHeight) {
    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    if (!dimensionsChanged && !symbolHeightChanged) return;

    this.dimensions = dimensions;
    this.symbolHeight = symbolHeight;
    if (this.retriggerSpine) this.applyScaleAndCenter();
  }

  updateDeviceInfo(device, orientation) {
    this.device = device;
    this.orientation = orientation;
    if (this.retriggerSpine) this.applyScaleAndCenter();
  }

  // ===================================================================
  //  ANIMATION
  // ===================================================================

  playAnimation() {
    if (!this.retriggerSpine?.state || !this.initialized || this.isPlaying)
      return;

    this.isPlaying = true;

    // Block all UI interaction
    if (this.dispatch) {
      this.dispatch(setCriticalAnimationPlaying(true));
    }

    // Fade in overlay
    this.overlayRef.current?.fadeInBackground();

    // Play retrigger SFX
    soundManager.play(SOUND_NAME.free_spin_retrigger);

    // Show spine on next frame (avoid flash)
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (!this.retriggerSpine) return;

      this.retriggerSpine.alpha = 1;
      this.retriggerSpine.state.clearTracks();
      this.retriggerSpine.state.clearListeners();
      this.retriggerSpine.state.apply(this.retriggerSpine.skeleton);
      this.retriggerSpine.skeleton.setToSetupPose();

      this.retriggerSpine.state.setAnimation(
        0,
        RETRIGGER_ANIMATION,
        false,
      );

      this.retriggerSpine.state.addListener({
        complete: (entry) => {
          if (
            entry.animation.name === RETRIGGER_ANIMATION &&
            this.isPlaying
          ) {
            this.finishAnimation();
          }
        },
      });
    });
  }

  finishAnimation() {
    if (!this.retriggerSpine || !this.initialized) return;

    this.isPlaying = false;
    this.retriggerSpine.alpha = 0.001;

    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop retrigger sound
    soundManager.stop(SOUND_NAME.free_spin_retrigger);

    // Unlock UI
    if (this.dispatch) {
      this.dispatch(setCriticalAnimationPlaying(false));
    }

    // Fade out overlay and close dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        this.dispatch(setShowFreeSpinRetrigger(false));
      }
    });
  }

  // ===================================================================
  //  CLEANUP
  // ===================================================================

  cleanup() {
    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_retrigger);

    if (this.retriggerSpine) {
      this.retriggerSpine.state?.clearListeners();
      this.retriggerSpine.state?.clearTracks();
      if (this.retriggerSpine.state) this.retriggerSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.retriggerSpine)) {
        this.container.removeChild(this.retriggerSpine);
      }
      this.retriggerSpine.destroy();
      this.retriggerSpine = null;
    }

    this.container = null;
    this.initialized = false;
  }
}

export default FreeSpinRetriggerController;
